'use client'

import {
  add,
  eachYearOfInterval,
  endOfDecade,
  format,
  isEqual,
  isFuture,
  parse,
  startOfDecade,
  startOfToday,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { cn } from '@/lib/cn'

import { Button, buttonVariants } from '../button'
import { Popover, PopoverContent, PopoverTrigger } from '../popover'

function getStartOfCurrentYear() {
  return new Date(startOfToday().getFullYear(), 0, 1)
}

type Props = {
  currentYear: Date
  onYearChange: (newYear: Date) => void

  disabled?: boolean
  className?: string
  isError?: boolean
}

const YearPicker = ({
  currentYear,
  onYearChange,
  disabled = false,
  className,
  isError = false,
}: Props) => {
  const [open, setOpen] = useState(false)

  const [currentDecade, setCurrentDecade] = useState(
    format(currentYear, 'yyyy'),
  )
  const firstYearCurrentDecade = startOfDecade(
    parse(currentDecade, 'yyyy', new Date()),
  )

  const years = eachYearOfInterval({
    start: firstYearCurrentDecade,
    end: endOfDecade(firstYearCurrentDecade),
  })

  const year = useMemo(() => {
    return format(currentYear, 'yyyy', { locale: ptBR })
  }, [currentYear])

  function previousDecade() {
    const firstYearPreviousDecade = add(firstYearCurrentDecade, { years: -10 })
    setCurrentDecade(format(firstYearPreviousDecade, 'yyyy'))
  }

  function nextDecade() {
    const firstYearNextDecade = add(firstYearCurrentDecade, { years: 10 })
    setCurrentDecade(format(firstYearNextDecade, 'yyyy'))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label="calendar"
          variant="secondary"
          disabled={disabled}
          className={cn(
            'justify-start text-left gap-2 border-system-border-primary font-normal text-system-text-primary',
            !currentYear && 'text-system-text-secondary',
            isError && 'border-system-state-error',
            className,
          )}
        >
          <CalendarIcon className="size-4" />
          {year}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <div className="flex flex-col space-y-4 sm:flex-row sm:space-x-4 sm:space-y-0">
          <div className="space-y-4">
            <div className="relative flex items-center justify-center">
              <div
                className="text-sm font-medium text-system-text-primary"
                aria-live="polite"
                role="presentation"
                id="year-picker"
              >
                {`${format(firstYearCurrentDecade, 'yyyy')} - ${format(
                  endOfDecade(firstYearCurrentDecade),
                  'yyyy',
                )}`}
              </div>
              <div className="flex items-center space-x-1">
                <button
                  name="previous-decade"
                  aria-label="Go to previous decade"
                  className={cn(
                    buttonVariants({ variant: 'secondary' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                    'absolute left-1',
                  )}
                  type="button"
                  onClick={previousDecade}
                >
                  <ChevronLeft className="size-4" />
                </button>
                <button
                  name="next-decade"
                  aria-label="Go to next decade"
                  className={cn(
                    buttonVariants({ variant: 'secondary' }),
                    'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
                    'absolute right-1 disabled:bg-slate-100',
                  )}
                  type="button"
                  disabled={isFuture(
                    add(firstYearCurrentDecade, { years: 10 }),
                  )}
                  onClick={nextDecade}
                >
                  <ChevronRight className="size-4" />
                </button>
              </div>
            </div>

            <div
              className="grid w-full grid-cols-3 gap-2"
              role="grid"
              aria-labelledby="year-picker"
            >
              {years.map((year) => (
                <div
                  key={year.toString()}
                  className="relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
                  role="presentation"
                >
                  <button
                    name="year"
                    className={cn(
                      'inline-flex h-9 w-16 items-center justify-center rounded-md p-0 text-sm font-normal ring-offset-white transition-colors hover:bg-slate-100 hover:text-system-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-selected:opacity-100 text-system-text-primary',
                      isEqual(year, currentYear) &&
                        'bg-system-primary-purple text-white hover:bg-system-primary-purple hover:text-white focus:bg-system-primary-purple focus:text-white',
                      !isEqual(year, currentYear) &&
                        isEqual(year, getStartOfCurrentYear()) &&
                        'bg-slate-100 text-system-text-primary',
                    )}
                    disabled={isFuture(year)}
                    role="gridcell"
                    aria-label={format(year, 'yyyy')}
                    tabIndex={-1}
                    type="button"
                    onClick={() => onYearChange(year)}
                  >
                    <time dateTime={format(year, 'yyyy')}>
                      {format(year, 'yyyy')}
                    </time>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
YearPicker.displayName = 'YearPicker'

export { YearPicker }
